import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { getBalance } from '@/redux/parcel/asyncRequest';
import { AppDispatch, RootState } from '@/redux/store';
import { IResConfirmOK } from '@/types/parcel/orders';
import { delay, updateReducerState } from '@/utils/common';

import type { SetStateAction } from '@/types/common/reducers.interface';
import type { IReqPickups, IResPickupsOK } from '@/types/parcel/orders/pickups.interface';

interface Props {
  orders: IResPickupsOK['data']['ordersData'] | [];
  selected: string[];
  fetchShipments: ({ search }: IReqPickups) => Promise<void>;
  onClose: () => void;
}

interface State {
  rescheduleDate: Date | null;
  successModal: boolean;
  availableToday: boolean;
  hasNAL: boolean;
  allAreNAL: boolean;
  selectedTemp: string[];
}

const initialState: State = {
  rescheduleDate: null,
  successModal: false,
  availableToday: true,
  hasNAL: false,
  allAreNAL: false,
  selectedTemp: [],
};

const reducer = (state: State, action: SetStateAction<State>): State => {
  switch (action.type) {
    case 'SET_STATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const useScheduleModal = (props: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const dispatchStore: AppDispatch = useDispatch();
  const { profile } = useSelector((state: RootState) => state.parcel);

  const updateState = (updates: Partial<State> | ((prevState: State) => Partial<State>)) => {
    updateReducerState(dispatch, state, updates);
  };

  const allSelectedOrders = useMemo(() => {
    return props.orders.filter((order) => props.selected.includes(order.trackingId));
  }, [props.orders, props.selected]);

  const fetchAvailableToday = useCallback(async (deliveryType: string): Promise<void> => {
    const http = initializeHttpProvider();
    try {
      const response = await http.orders.confirmToday({ deliveryType });

      updateState({
        availableToday: (response as IResConfirmOK).data,
      });
    } catch {
      toast.error('Ha ocurrido un error, por favor intenta de nuevo');
    }
  }, []);

  const reschedule = useCallback(
    async (trackingId: string) => {
      const http = initializeHttpProvider();
      await http.orders.reschedule({
        trackingId,
        pickUpAfter: format(state.rescheduleDate!, 'yyyy-MM-dd'),
      });
    },
    [state.rescheduleDate],
  );

  const confirm = useCallback(
    async (trackingId: string) => {
      const http = initializeHttpProvider();
      await http.orders.confirm({
        trackingId,
        pickUpAfter: format(state.rescheduleDate!, 'yyyy-MM-dd'),
      });
    },
    [state.rescheduleDate],
  );

  const confirmAndReschedule = useCallback(async () => {
    if (!state.rescheduleDate) return;

    try {
      for (const order of allSelectedOrders) {
        try {
          if (order.status !== '1001') {
            await reschedule(order.trackingId);
            continue;
          }

          await confirm(order.trackingId);

          await delay(100);
        } catch (e) {
          console.warn(e);
          throw e;
        }
      }

      await props.fetchShipments({});

      // Solo actualizar balance si es cuenta prepago
      if (profile.data?.configuration.paymentType === 'PREPAID') {
        dispatchStore(getBalance());
      }

      props.onClose();
      openSuccessModal();
    } catch {
      toast.error('Ha ocurrido un error, por favor intenta de nuevo');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.rescheduleDate,
    allSelectedOrders,
    confirm,
    reschedule,
    props,
    dispatchStore,
    profile.data,
  ]);

  const onChgReschedule = useCallback((value: Date | null) => {
    updateState({ rescheduleDate: value });
  }, []);

  const cleanReschedule = useCallback(() => {
    updateState({ rescheduleDate: null });
  }, []);

  const openSuccessModal = useCallback(() => {
    updateState({ successModal: true });
  }, []);

  const closeSuccessModal = useCallback(() => {
    updateState({ successModal: false, rescheduleDate: null });
  }, []);

  useEffect(() => {
    const nalOrders = allSelectedOrders.filter((order) => order.deliveryType === 'NAL');

    updateState({
      hasNAL: nalOrders.length > 0 && nalOrders.length < props.selected.length,
      allAreNAL: nalOrders.length === props.selected.length,
    });
  }, [allSelectedOrders]);

  useEffect(() => {
    for (const order of allSelectedOrders) {
      void fetchAvailableToday(order.deliveryType);
    }
  }, [allSelectedOrders]);

  useEffect(() => {
    if (props.selected.length > 0) {
      updateState({ selectedTemp: props.selected });
    }
  }, [props.selected]);

  return {
    ...state,
    onChgReschedule,
    openSuccessModal,
    closeSuccessModal,
    cleanReschedule,
    confirmAndReschedule,
  };
};

export default useScheduleModal;
